<template>
  <div>
    <d-row no-gutters class="page-header py-4 mb-2">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <template v-if="isInPopup">
          <h4>Opinie pacjenta</h4>
        </template>
        <template v-else>
          <h4>Lista opinii pacjenta:
            <p class="mt-1 mb-0 patient-name">{{ patient.name + ' ' + patient.last_name }}</p>
          </h4>
        </template>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="showAddOpinionPopup"
          :disabled="!loaded">
          <i class="material-icons">add</i>
          Dodaj opinię
        </d-button>
      </d-col>
    </d-row>
    <d-card
      class="w-100"
      style="box-shadow: none;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">Data wystawienia</th>
            <th scope="col" class="border-0" style="min-width: 7rem">Data wizyty</th>
            <th scope="col" class="border-0">Rodzaj opinii</th>
            <th scope="col" class="border-0">Kto wystawił</th>
            <th scope="col" class="border-0 text-center">Pobierz</th>
            <th scope="col" class="border-0 text-center">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(opinion, opinionIndex) of opinions"
            :key="'opinion-' + opinionIndex">
            <td class="align-middle">
              {{opinion.created_at.slice(0, 10)}}
            </td>
            <td class="align-middle">
              <template v-if="opinion.visit_date !== null">
                <p style="color: #17c671">{{ opinion.visit_date.slice(0, 11) }}</p>
                <span style="margin-top: -30px; display: block; font-size: 0.9rem; opacity: 0.6">
                  {{ opinion.visit_date.slice(11, 16) }} - {{ opinion.visit_date.slice(11, 16) }}
                </span>
              </template>
              <template v-else>
                <span style="opacity: 0.5">Brak</span>
              </template>
            </td>
            <td class="align-middle">
              <template v-if="opinion.opinion_name !== ''">
                <span style="text-transform: uppercase">{{opinion.opinion_name.slice(0, 1)}}</span><span>{{opinion.opinion_name.slice(1)}}</span>
              </template>
              <template v-else>
                <span style="opacity: 0.5">Brak</span>
              </template>
            </td>
            <td class="align-middle">
              {{opinion.user_name}}
            </td>
            <td class="align-middle text-center">
              <d-button
                theme="info"
                outline
                :disabled="actionInProgress !== false"
                @click.prevent="downloadOpinion(opinion.id)">
                <template v-if="actionInProgress !== opinion.id">
                  <i class="material-icons">cloud_download</i>
                  <span v-if="!isInPopup" class="ml-2">Pobierz</span></template>
                <template v-else>
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                </template>
              </d-button>
            </td>
            <td
              class="align-middle text-center"
              style="width: 130px;">
              <d-button
                theme="primary"
                @click.prevent="showEditOpinionPopup(opinion.id)"
                class="mr-2"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.prevent="removeOpinion(opinion.id)"
                title="Usuń">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="opinions.length === 0">
            <td
              colspan="6"
              class="text-center">
              Brak opinii do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <patients-form-opinions-popup
      v-if="isShowOpinionPopup"
      :all-institutions="allInstitutions"
      :on-close-popup="handleCloseOpinionPopup"
      :on-submit-callback="loadItemsData"
      :patient-id="patient.id"
      :edit-opinion-id="editOpinionID || 0"
      :show="true"/>
  </div>
</template>

<script>
import Vue from 'vue';
import ListUtils from '@/utils/ListUtils.js';
import PatientsFormOpinionsPopup from '@/components/forms/patients/PatientsFormOpinionsPopup.vue';

export default {
  name: 'patients-form-opinions',
  props: {
    filterForVisit: {
      default: false,
      type: Boolean
    },
    isInPopup: {
      default: false,
      type: Boolean
    },
    isVisible: {
      default: false,
      type: Boolean
    },
    patient: {
      required: true,
      type: Object
    },
    visitId: {
      defualt: 0,
      type: Number
    },
    allInstitutions: {
      required: true,
      type: Array
    },
  },
  components: {
    'patients-form-opinions-popup': PatientsFormOpinionsPopup
  },
  watch: {
    'isVisible': function (newValue) {
      if (newValue && !this.loaded) {
        this.loadItemsData();
      }
    }
  },
  data () {
    return {
      actionInProgress: false,
      editOpinionID: null,
      isShowOpinionPopup: false,
      loaded: false,
      loadError: false,
      opinions: [],
      removeInProgress: false
    }
  },
  mounted () {
    if (this.isVisible) {
      this.loadItemsData();
    }
  },
  methods: {
    loadItemsData () {
      Vue.set(this, 'loaded', false);
      Vue.set(this, 'loadError', false);

      let where = {};
      if (this.filterForVisit) {
        where.visit_id = this.visitId;
      }
      ListUtils.loadItemsData(this, {
        endpoint: '/patients/opinions/items?patient_id=' + this.patient.id,
        listField: 'opinions',
        pagination: false,
        params: {
          where
        }
      });
    },
    removeOpinion (opinionID) {
      Vue.set(this, 'removeInProgress', true);

      ListUtils.removeItem(this, {
        endpointBase: '/patients/opinions/delete/',
        id: opinionID,
        successTitle: 'Usunięto opinie',
        successText: 'Wybrana opinia została usunięta',
        confirmTitle: 'Czy na pewno chcesz usunąć tą opinie?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie opinii nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadItemsData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    downloadOpinion (opinionID) {
      this.actionInProgress = true;

      this.$http.post('/patients/opinions/get-file/' + opinionID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = 'Opinia-nr-';
        link.setAttribute('download', filename + opinionID + '.pdf');
        document.body.appendChild(link);
        link.addEventListener('click', this.clickCallback, false);
        link.click();
        document.body.removeChild(link);
        this.actionInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'Wystąpił błąd podczas pobierania',
          html: 'Spróbuj ponownie',
          type: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
        this.actionInProgress = false;
      });
    },
    clickCallback (e) {
      e.stopPropagation();
    },
    showAddOpinionPopup () {
      Vue.set(this, 'editOpinionID', null);
      Vue.set(this, 'isShowOpinionPopup', true);
    },
    showEditOpinionPopup (opinionID) {
      Vue.set(this, 'isShowOpinionPopup', true);
      Vue.set(this, 'editOpinionID', opinionID);
    },
    handleCloseOpinionPopup () {
      Vue.set(this, 'isShowOpinionPopup', false);
      Vue.set(this, 'editOpinionID', null);
    }
  }
}
</script>
